import { collection, deleteDoc, deleteField, doc, query, runTransaction, updateDoc } from "firebase/firestore";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfirmDialog, ErrorText, LoadingView, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import { useCollection, useDoc } from "@/hooks/firestoreHooks";
import {
  ATHLETE_CATEGORIES,
  ATHLETE_CATEGORY_LABELS,
  ATHLETE_CATEGORY_MAX_LENGTH,
  formatGPSID,
  parseAssignmentState,
  parseTrackerDocument,
  trackerStatus,
  type GPSTrackerDocument,
} from "@/models/gps";
import {
  sortPlannerDocuments,
  validateGPSIDInput,
  type GPSPlannerDocument,
} from "@/models/gpsAssignmentPlanner";
import { applyGPSIDAssignment } from "./gpsAssignmentWriter";
import { useCompetitionDisplay } from "./useCompetitionDisplay";
import "@/features/home/home.css";

// Port of GPSAssignmentView: per-field assignment of gps-id / gps-alias /
// athlete-cat on tracker docs. The GPS ID apply runs the swap/backfill
// planner in one transaction (assigning an in-use ID swaps the two devices);
// alias/category writes stay field-scoped so the rules diff check passes.

const CUSTOM_CATEGORY_OPTION = "__custom__";

type SaveKind = "gpsID" | "alias" | "category" | "action";

interface AssignmentRow extends GPSPlannerDocument {
  tracker: GPSTrackerDocument;
}

export function GpsAssignments() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { display, members } = useCompetitionDisplay(instanceId);

  const trackersQuery = instanceId ? query(collection(db, "leagueCompetitionInstances", instanceId, "gpsData")) : null;
  const trackers = useCollection(trackersQuery, parseTrackerDocument, `gpsData:${instanceId}`);
  const assignmentState = useDoc(
    instanceId ? doc(db, "leagueCompetitionInstances", instanceId, "gpsAssignmentState", "default") : null,
    parseAssignmentState,
  );

  const [drafts, setDrafts] = useState<Record<string, { gpsID: string; alias: string; athleteCat: string }>>({});
  // Trackers whose category select is in "Custom…" mode (free-form identifier).
  const [customCategoryMode, setCustomCategoryMode] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<{ id: string; kind: SaveKind } | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ id: string; action: "deactivate" | "remove" } | null>(null);

  const rows = useMemo<AssignmentRow[]>(() => {
    const memberName = (userID: string) => members.find((member) => member.userID === userID)?.displayName;
    const mapped = trackers.data
      // iOS parity: only devices that have reported a location are listed.
      .filter((tracker) => tracker.latestSample !== undefined)
      .map((tracker) => ({
        id: tracker.id,
        displayName: tracker.displayName ?? memberName(tracker.userID) ?? tracker.userID.slice(0, 8),
        gpsID: tracker.gpsID,
        tracker,
      }));
    return sortPlannerDocuments(mapped);
  }, [trackers.data, members]);

  if (!instanceId) return null;

  const hiddenCount = trackers.data.length - rows.length;

  const draftFor = (tracker: GPSTrackerDocument) =>
    drafts[tracker.id] ?? { gpsID: tracker.gpsID ?? "", alias: tracker.gpsAlias ?? "", athleteCat: tracker.athleteCat ?? "" };

  const setDraft = (id: string, value: { gpsID: string; alias: string; athleteCat: string }) =>
    setDrafts((previous) => ({ ...previous, [id]: value }));

  const clearDraft = (id: string) =>
    setDrafts((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });

  const setRowError = (id: string, message: string | null) =>
    setRowErrors((previous) => {
      const next = { ...previous };
      if (message) next[id] = message;
      else delete next[id];
      return next;
    });

  const runSave = async (id: string, kind: SaveKind, work: () => Promise<string | null>) => {
    setRowError(id, null);
    setStatusMessage(null);
    setSaving({ id, kind });
    try {
      const message = await work();
      if (message) setStatusMessage(message);
    } catch (err) {
      setRowError(id, err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(null);
    }
  };

  const applyGPSID = (row: AssignmentRow, normalized: string) =>
    runSave(row.id, "gpsID", async () => {
      await applyGPSIDAssignment({
        instanceId,
        targetDocumentID: row.id,
        desiredGPSID: normalized,
        candidates: rows.map(({ id, displayName }) => ({ id, displayName })),
      });
      setDrafts((previous) => {
        const current = previous[row.id];
        if (!current) return previous;
        return { ...previous, [row.id]: { ...current, gpsID: normalized } };
      });
      return `GPS ID ${normalized} assigned.`;
    });

  const applyAlias = (row: AssignmentRow, alias: string) =>
    runSave(row.id, "alias", async () => {
      await updateDoc(doc(db, "leagueCompetitionInstances", instanceId, "gpsData", row.id), {
        "gps-alias": alias ? alias : deleteField(),
      });
      return alias ? `GPS Alias updated to ${alias}.` : "GPS Alias cleared.";
    });

  const applyCategory = (row: AssignmentRow, category: string) =>
    runSave(row.id, "category", async () => {
      await updateDoc(doc(db, "leagueCompetitionInstances", instanceId, "gpsData", row.id), {
        "athlete-cat": category ? category : deleteField(),
      });
      return category ? `Athlete Category updated to ${category}.` : "Athlete Category cleared.";
    });

  /** Reserve the next GPS ID (>= current counter and > any assigned ID). */
  const reserveNextGPSID = async (): Promise<string> => {
    const stateRef = doc(db, "leagueCompetitionInstances", instanceId, "gpsAssignmentState", "default");
    const highestAssigned = Math.max(0, ...trackers.data.map((t) => Number(t.gpsID ?? 0)));
    return runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(stateRef);
      const currentNext = snapshot.exists() ? ((snapshot.data().nextGPSID as number) ?? 1) : 1;
      const planned = Math.max(currentNext, highestAssigned + 1);
      if (planned > 999) throw new Error("All GPS IDs (001–999) are assigned.");
      transaction.set(stateRef, { id: "default", nextGPSID: planned + 1, updatedAt: new Date() });
      return formatGPSID(planned);
    });
  };

  const autoAssign = (row: AssignmentRow) =>
    runSave(row.id, "gpsID", async () => {
      const gpsID = await reserveNextGPSID();
      await updateDoc(doc(db, "leagueCompetitionInstances", instanceId, "gpsData", row.id), { "gps-id": gpsID });
      return `GPS ID ${gpsID} assigned.`;
    });

  const deactivate = (row: AssignmentRow) =>
    runSave(row.id, "action", async () => {
      await updateDoc(doc(db, "leagueCompetitionInstances", instanceId, "gpsData", row.id), { isTransmitting: false });
      return `${row.displayName} marked inactive.`;
    });

  const remove = (row: AssignmentRow) =>
    runSave(row.id, "action", async () => {
      await deleteDoc(doc(db, "leagueCompetitionInstances", instanceId, "gpsData", row.id));
      clearDraft(row.id);
      return `${row.displayName} removed from GPS data.`;
    });

  const confirmingRow = confirming ? rows.find((row) => row.id === confirming.id) : undefined;

  const now = new Date();

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <div>
          <h1>GPS Assignments</h1>
          <p className="home-hint">
            {display?.eventName}
            {assignmentState.data ? ` · next auto ID ${formatGPSID(assignmentState.data.nextGPSID)}` : ""}
          </p>
        </div>
      </div>

      <ErrorText>{trackers.error}</ErrorText>
      {statusMessage && <p className="home-hint">{statusMessage}</p>}

      {trackers.loading && trackers.data.length === 0 ? (
        <LoadingView />
      ) : rows.length === 0 ? (
        <SBCard>
          <p className="home-hint">
            {trackers.data.length === 0
              ? "No tracker documents yet. Trackers appear once athletes start transmitting from the iOS app."
              : "No initialized GPS devices. Devices appear once they report a location."}
          </p>
        </SBCard>
      ) : (
        <SBCard className="home-card">
          {rows.map((row) => {
            const tracker = row.tracker;
            const draft = draftFor(tracker);
            const status = trackerStatus(tracker, now);
            const validation = validateGPSIDInput(draft.gpsID, row, rows);
            const rowSaving = saving?.id === row.id ? saving.kind : null;
            const gpsIDApplyEnabled =
              (validation.kind === "ready" || validation.kind === "swap") && saving?.kind !== "gpsID";
            const trimmedAlias = draft.alias.trim();
            const aliasApplyEnabled = trimmedAlias !== (tracker.gpsAlias ?? "") && saving?.kind !== "alias";
            const categoryApplyEnabled = draft.athleteCat !== (tracker.athleteCat ?? "") && saving?.kind !== "category";
            const customCategory =
              customCategoryMode[tracker.id] ||
              (!!draft.athleteCat && !(ATHLETE_CATEGORIES as string[]).includes(draft.athleteCat));
            return (
              <div key={tracker.id} className="event-row" style={{ cursor: "default", flexWrap: "wrap", gap: 10 }}>
                <Link
                  to={`/competitions/${instanceId}/gps/devices/${tracker.id}`}
                  className="event-row-main"
                  style={{ flex: "1 1 200px", color: "inherit", textDecoration: "none" }}
                >
                  <strong>{row.displayName}</strong>
                  <span>
                    {tracker.deviceName ?? "Device"} · {status}
                    {tracker.lastUpdatedAt ? ` · ${tracker.lastUpdatedAt.toLocaleTimeString()}` : ""}
                    {" · Details ›"}
                  </span>
                </Link>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        value={draft.gpsID}
                        placeholder="001"
                        maxLength={3}
                        inputMode="numeric"
                        aria-label="GPS ID"
                        style={{ width: 64, textAlign: "center" }}
                        onChange={(e) => setDraft(tracker.id, { ...draft, gpsID: e.target.value.replace(/[^0-9]/g, "") })}
                      />
                      <SecondaryButton
                        title={rowSaving === "gpsID" ? "Applying…" : "Apply"}
                        disabled={!gpsIDApplyEnabled}
                        onClick={() => {
                          if (validation.kind === "ready" || validation.kind === "swap") {
                            void applyGPSID(row, validation.normalized);
                          }
                        }}
                      />
                      {!tracker.gpsID && (
                        <SecondaryButton
                          title="Auto-assign"
                          disabled={saving?.kind === "gpsID"}
                          onClick={() => void autoAssign(row)}
                        />
                      )}
                    </div>
                    <span className="home-hint" style={{ fontSize: 12 }}>
                      {validation.message}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        value={draft.alias}
                        placeholder={row.displayName}
                        aria-label="GPS alias"
                        style={{ width: 140 }}
                        onChange={(e) => setDraft(tracker.id, { ...draft, alias: e.target.value })}
                      />
                      <SecondaryButton
                        title={rowSaving === "alias" ? "Applying…" : "Apply"}
                        disabled={!aliasApplyEnabled}
                        onClick={() => void applyAlias(row, trimmedAlias)}
                      />
                    </div>
                    <span className="home-hint" style={{ fontSize: 12 }}>
                      {aliasApplyEnabled ? "Ready" : "No change"}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <select
                        value={customCategory ? CUSTOM_CATEGORY_OPTION : draft.athleteCat}
                        style={{ width: 150 }}
                        onChange={(e) => {
                          if (e.target.value === CUSTOM_CATEGORY_OPTION) {
                            setCustomCategoryMode((previous) => ({ ...previous, [tracker.id]: true }));
                            if ((ATHLETE_CATEGORIES as string[]).includes(draft.athleteCat)) {
                              setDraft(tracker.id, { ...draft, athleteCat: "" });
                            }
                          } else {
                            setCustomCategoryMode((previous) => ({ ...previous, [tracker.id]: false }));
                            setDraft(tracker.id, { ...draft, athleteCat: e.target.value });
                          }
                        }}
                        aria-label="Athlete category"
                      >
                        <option value="">— Category —</option>
                        {ATHLETE_CATEGORIES.map((category) => (
                          <option key={category} value={category}>
                            {ATHLETE_CATEGORY_LABELS[category]}
                          </option>
                        ))}
                        <option value={CUSTOM_CATEGORY_OPTION}>Custom…</option>
                      </select>
                      {customCategory && (
                        <input
                          value={draft.athleteCat}
                          placeholder="Custom category"
                          maxLength={ATHLETE_CATEGORY_MAX_LENGTH}
                          style={{ width: 130 }}
                          aria-label="Custom athlete category"
                          onChange={(e) => setDraft(tracker.id, { ...draft, athleteCat: e.target.value.trim() })}
                        />
                      )}
                      <SecondaryButton
                        title={rowSaving === "category" ? "Applying…" : "Apply"}
                        disabled={!categoryApplyEnabled}
                        onClick={() => void applyCategory(row, draft.athleteCat)}
                      />
                    </div>
                    <span className="home-hint" style={{ fontSize: 12 }}>
                      {categoryApplyEnabled ? "Ready" : "No change"}
                    </span>
                  </div>
                  {tracker.isTransmitting ? (
                    <SecondaryButton
                      title="Deactivate"
                      destructive
                      disabled={rowSaving === "action"}
                      onClick={() => setConfirming({ id: row.id, action: "deactivate" })}
                    />
                  ) : (
                    <SecondaryButton
                      title="Remove"
                      destructive
                      disabled={rowSaving === "action"}
                      onClick={() => setConfirming({ id: row.id, action: "remove" })}
                    />
                  )}
                </div>
                <ErrorText>{rowErrors[row.id]}</ErrorText>
              </div>
            );
          })}
        </SBCard>
      )}

      {hiddenCount > 0 && rows.length > 0 && (
        <p className="home-hint">
          {hiddenCount} device{hiddenCount === 1 ? "" : "s"} hidden until they report a location.
        </p>
      )}

      <ConfirmDialog
        open={confirming !== null && confirmingRow !== undefined}
        title={confirming?.action === "remove" ? "Remove GPS Device?" : "Remote Deactivate Tracking?"}
        message={
          confirming?.action === "remove"
            ? `This will remove ${confirmingRow?.displayName ?? "this device"}'s GPS document and its recorded samples from this competition. Removal is only allowed when the device is inactive.`
            : `This will mark ${confirmingRow?.displayName ?? "this device"}'s GPS document as inactive. If the transmitting phone is still running, it may write again until tracking is stopped there.`
        }
        confirmTitle={confirming?.action === "remove" ? "Remove" : "Deactivate"}
        destructive
        busy={saving?.kind === "action"}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (!confirming || !confirmingRow) return;
          const action = confirming.action === "remove" ? remove : deactivate;
          void action(confirmingRow).finally(() => setConfirming(null));
        }}
      />
    </div>
  );
}
